import type { Lang } from '../i18n'
import data from './changelog.json'

/** One sentence in both languages: the site is prerendered in each, from one file. */
export type Localized = Record<Lang, string>

export type ChangelogEntry = {
  /** When the change landed in the repo, YYYY-MM-DD. */
  date: string
  /** The ronin-v version it shipped in, or null while unreleased. Stamped at release time. */
  version: string | null
  area?: 'app' | 'web'
  title: Localized
  description: Localized
}

/**
 * Typed view of content/changelog.json. The JSON is the source of truth so that the release
 * skill can stamp `version` without touching TypeScript, and so adding an entry never means
 * editing code (see the changelog skill in .claude/skills). Same shape as the DiabetesNinja site's file, except that
 * title and description are sv/en pairs.
 *
 * Sorted newest-first here rather than trusting the file's order, since entries get appended
 * by hand and a stale date is easier to miss than a mis-sorted list.
 */
export const changelog: ChangelogEntry[] = [...(data.entries as ChangelogEntry[])].sort((a, b) =>
  a.date === b.date ? 0 : a.date < b.date ? 1 : -1,
)

/** The newest entry's date, which is when the page last changed. Feeds the sitemap's lastmod. */
export const latestChangelogDate: string | undefined = changelog[0]?.date

export type NewsCopy = {
  title: string
  lead: string
  back: string
  /** Prefix for the version pill: "Version 1.0.8". */
  versionLabel: string
  unreleased: string
  areaApp: string
  areaWeb: string
}

const en: NewsCopy = {
  title: "What's new",
  lead: 'What is new and fixed in Ronin, newest first.',
  back: 'Back to Ronin',
  versionLabel: 'Version',
  unreleased: 'Coming in the next version',
  areaApp: 'The app',
  areaWeb: 'The site',
}

const sv: NewsCopy = {
  title: 'Nyheter',
  lead: 'Vad som är nytt och fixat i Ronin, senast först.',
  back: 'Tillbaka till Ronin',
  versionLabel: 'Version',
  unreleased: 'Kommer i nästa version',
  areaApp: 'Appen',
  areaWeb: 'Webben',
}

export const newsCopy: Record<Lang, NewsCopy> = { sv, en }
